/** 行业研报(产业链、行业主题)/ 公司研报(只针对一家公司) */
export type ReportKind = 'industry' | 'company'

export const REPORT_KIND_LABEL: Record<ReportKind, string> = { industry: '行业研报', company: '公司研报' }

export interface Document {
  id: string
  filename: string
  size_bytes: number
  chunk_count: number
  created_at: string
  /** 本地缓存里的旧列表没有这个字段,读的时候按 industry 处理(见 docKind)。 */
  kind?: ReportKind
  /** 最新版本号。本地缓存里的旧列表没有这个字段,读的时候按 1 处理。 */
  latest_version?: number
  /** 最新版本的上传时间 */
  updated_at?: string
}

export function docKind(doc: Pick<Document, 'kind'>): ReportKind {
  return doc.kind ?? 'industry'
}

/** 上传前查重列出的候选:库里与这篇最相近的几篇之一 */
export interface SimilarReport {
  document: Document
  /** 0~1,1 = 去掉版本标记后标题一字不差 */
  titleScore: number
  /** 0~1,正文重合度;不同研报之间通常只有 2%~3% */
  contentScore: number
  /** 主题相近度(embedding 余弦),只用于排序;服务端向量不可用时为 null */
  topicScore: number | null
  /** 服务端的标题判断:去掉版本标记后相同 / 相近 / 没对上 */
  titleMatch: 'same' | 'similar' | null
  /** 正文重合过线 */
  contentMatch: boolean
  /** titleMatch 或 contentMatch:很可能就是这篇的旧版本 */
  likely: boolean
}

/** 文档的一个版本 */
export interface DocumentVersion {
  doc_id: string
  version: number
  filename: string
  size_bytes: number
  chunk_count: number
  /** 更新说明 */
  note: string | null
  created_at: string
}
export interface Source {
  section_title: string
  section_slug: string
  chunk_index: number
}
export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  /** 回答时查的是哪个版本;版本化之前的旧消息没有 */
  version?: number
}

export type TraceStatus = 'ok' | 'degraded' | 'error'

export interface TraceRecord {
  id: string
  route: string
  user_id: string | null
  status: TraceStatus
  duration_ms: number
  span_count: number
  degraded_count: number
  error_count: number
  started_at: string
  created_at: string
}

export interface SpanRecord {
  id: string
  trace_id: string
  parent_span_id: string | null
  name: string
  status: TraceStatus
  start_offset_ms: number
  duration_ms: number
  degraded_reason: string | null
  input: string | null
  output: string | null
  /** JSON 字符串 */
  metadata: string
  error_message: string | null
}

export interface WaterfallRow {
  span: SpanRecord
  depth: number
  leftPct: number
  widthPct: number
}

export type EvalStatus = 'none' | 'running' | 'done' | 'failed'

export interface EvalReportRow {
  doc_id: string
  filename: string
  status: EvalStatus
  question_count: number
  avg_recall: number | null
  avg_precision: number | null
  avg_faithfulness: number | null
  avg_relevancy: number | null
  finished_at: string | null
  /** 这份分数是哪个版本的;旧记录为 null */
  run_version: number | null
  latest_version: number
}

export interface EvalStats {
  docsEvaluated: number
  avgRecall: number
  avgPrecision: number
  avgFaithfulness: number
  avgRelevancy: number
}

export interface EvalResultRow {
  question: string
  expected: string
  retrieved_sections: string
  answer: string
  recall: number
  precision: number
  faithfulness: number
  relevancy: number
  reasoning: string
}

export interface LLMPreset {
  id: string
  label: string
  kind: 'openai' | 'anthropic'
  suggestedModels: string[]
  custom: boolean
}

export interface LLMConfigView {
  providerId: string
  baseURL: string | null
  model: string
  keyHint: string
  enabled: boolean
  updatedAt: string
}

export interface LLMEffective {
  model: string
  source: 'user' | 'server'
  providerId: string
}

export interface LLMConfigResponse {
  available: boolean
  presets: LLMPreset[]
  config: LLMConfigView | null
  effective: LLMEffective | null
  configError: string | null
}

export interface LLMSaveInput {
  providerId: string
  baseURL?: string
  model: string
  apiKey?: string
  enabled?: boolean
}

/** GET /api/site-model —— 站点默认模型(管理员)。 */
export interface SiteModelResponse {
  /** 站长未配任何 key 时为 null(此时 configError 非空) */
  providerId: string | null
  /** 下一次提问生效的模型配置,原始字符串;.env 配了降级链时形如 `a,b` */
  model: string | null
  /** db = 管理员在界面上覆盖过;env = 仍来自 .env */
  source: 'db' | 'env'
  envModel: string | null
  suggestedModels: string[]
  configError: string | null
}

export interface SignalSide {
  trend: 1 | -1
  barDate: string
  stopLine: number
  closeAdj: number
  atr: number
  distPct: number
  flipDate: string | null
  flipPrice: number | null
  heldDays: number | null
  sinceFlipPct: number | null
}

export interface SignalRow {
  symbol: string
  name: string | null
  market: 'US' | 'HK'
  currency: string | null
  closeRaw: number | null
  daily: SignalSide | null
  weekly: SignalSide | null
  divergent: boolean
  flips90d: number
  sourceDoc: { id: string; filename: string } | null
  sourceText: string | null
  /** 恒为 true —— 接口只返回未删除的行。保留字段是为了与服务端结构对齐 */
  enabled: boolean
  status: 'ok' | 'invalid' | 'insufficient'
  lastError: string | null
  lastScanAt: string | null
}

export interface ScanSummary {
  total: number
  ok: number
  failed: number
  insufficient: number
}

export interface SignalStateRow {
  symbol: string
  timeframe: '1d' | '1wk'
  bar_date: string
  trend: 1 | -1
  stop_line: number
  close_adj: number
  close_raw: number
  atr: number
}

export interface SignalEventRow {
  symbol: string
  /** 自选股里的公司名;尚未扫到名称时为 null —— 只显示代码 */
  name: string | null
  timeframe: '1d' | '1wk'
  bar_date: string
  direction: 1 | -1
  price: number
}

export interface ProbeResult {
  symbol: string
  market: 'US' | 'HK'
  name: string | null
  currency: string | null
  exchange: string | null
  /** 可用的已收盘日线根数 */
  bars: number
  /** 是否达到预热门槛。false 不阻止添加,只提示 */
  enough: boolean
  alreadyListed: boolean
  /** 曾被删除。添加即复活 */
  deleted: boolean
}

/** GET /api/holdings 的一行。weight 是市值占合计的比例,合计为 0 时为 0。 */
export interface HoldingPosition {
  symbol: string
  name: string | null
  shares: number
  marketValue: number
  cost: number
  pnl: number
  weight: number
}

/** 整份快照共用一个 updatedAt。没有持仓时 updatedAt 为 null、holdings 为空。 */
export interface HoldingsSnapshot {
  updatedAt: string | null
  holdings: HoldingPosition[]
}

/** GET /api/signals/watchlist/search 的一条候选 */
export interface SymbolSearchHit {
  symbol: string
  name: string | null
  market: 'US' | 'HK'
  exchange: string | null
}
